import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Play, Flame, Bell, Star } from "lucide-react";
import { PomodoroStartMenu } from "../PomodoroTimer";

// Title area of an ItemRow: inline rename editor, pencil button, the pencil
// action menu (Rename / Start focus / Edit schedule / Edit alert / Ask cover),
// the Pomodoro chooser popover, and the alert lead-minutes picker (anchored
// here but also openable from the alert chip in RowChips via shared state).
// Shared enter/exit motion for the inline popovers (stable reference).
const POPOVER_MOTION = {
    initial: { opacity: 0, y: -4, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -4, scale: 0.95 },
    transition: { duration: 0.15 },
};

export default function RowTitle({
    testId, title, editableTitle, completed,
    onEditTitle, onStartFocus, onEditSchedule, onEditLead, onAskCover,
    showPencilMenu, currentLead,
    leadPickerOpen, setLeadPickerOpen,
}) {
    const [titleEditing, setTitleEditing] = useState(false);
    const [titleDraft, setTitleDraft] = useState(editableTitle ?? title ?? "");
    const titleInputRef = useRef(null);
    const [pencilMenuOpen, setPencilMenuOpen] = useState(false);
    const [pomodoroChooserOpen, setPomodoroChooserOpen] = useState(false);

    useEffect(() => {
        if (titleEditing && titleInputRef.current) {
            titleInputRef.current.focus();
            try { titleInputRef.current.select?.(); } catch { /* time inputs may refuse select() — harmless */ }
        }
    }, [titleEditing]);

    const startEditing = () => {
        setTitleDraft(editableTitle ?? title ?? "");
        setTitleEditing(true);
    };

    const commitTitle = async () => {
        const next = (titleDraft || "").trim();
        const baseline = (editableTitle ?? title ?? "").trim();
        if (!next || next === baseline) { setTitleEditing(false); return; }
        try { await onEditTitle?.(next); } catch (e) { console.warn("title edit failed (parent shows toast)", e); }
        setTitleEditing(false);
    };
    const cancelTitle = () => { setTitleDraft(editableTitle ?? title ?? ""); setTitleEditing(false); };

    if (titleEditing) {
        return (
            <input
                ref={titleInputRef}
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commitTitle();
                    if (e.key === "Escape") cancelTitle();
                }}
                className="w-full bg-mustard-soft/60 rounded-md px-2 py-0.5 font-body text-warm-900 outline-none"
                data-testid={`${testId}-title-input`}
                aria-label="Edit title"
            />
        );
    }

    return (
        <div className="flex items-center gap-1.5 min-w-0" data-testid={`${testId}-title`}>
            <span
                className={`font-body text-warm-900 truncate ${
                    completed ? "line-through-soft text-warm-500" : ""
                } ${onEditTitle && !completed ? "cursor-text" : ""}`}
                onDoubleClick={() => {
                    if (onEditTitle && !completed) startEditing();
                }}
                title={onEditTitle && !completed ? "Double-click to rename" : undefined}
            >
                {title}
            </span>
            {onEditTitle && !completed && (
                <span className="relative inline-block shrink-0">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (showPencilMenu) {
                                setPencilMenuOpen((v) => !v);
                            } else {
                                startEditing();
                            }
                        }}
                        className="inline-flex items-center align-baseline p-0.5 rounded-full text-warm-500/60 hover:text-terracotta hover:bg-surface transition-all"
                        data-testid={`${testId}-edit-title`}
                        aria-label={showPencilMenu ? "Edit task" : "Edit title"}
                        title={showPencilMenu ? "Edit task" : "Edit title"}
                    >
                        <Pencil size={11} />
                    </button>
                    <AnimatePresence>
                        {pencilMenuOpen && (
                            <motion.div
                                key="pencil-menu"
                                {...POPOVER_MOTION}
                                className="absolute left-0 top-full mt-1 z-40 bg-cream rounded-2xl shadow-float border border-warm-300/40 py-1 w-44"
                                data-testid={`${testId}-pencil-menu`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={() => { setPencilMenuOpen(false); startEditing(); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left font-body text-sm text-warm-900 hover:bg-mustard-soft/40 transition-colors"
                                    data-testid={`${testId}-menu-rename`}
                                >
                                    <Pencil size={13} className="text-warm-500" />
                                    Rename
                                </button>
                                {onStartFocus && (
                                    <button
                                        onClick={() => { setPencilMenuOpen(false); setPomodoroChooserOpen(true); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left font-body text-sm text-warm-900 hover:bg-mustard-soft/40 transition-colors"
                                        data-testid={`${testId}-menu-focus`}
                                    >
                                        <Play size={13} className="text-terracotta" />
                                        Start focus
                                    </button>
                                )}
                                {onEditSchedule && (
                                    <button
                                        onClick={() => { setPencilMenuOpen(false); onEditSchedule(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left font-body text-sm text-warm-900 hover:bg-mustard-soft/40 transition-colors"
                                        data-testid={`${testId}-menu-schedule`}
                                    >
                                        <Flame size={13} className="text-terracotta" />
                                        Edit schedule
                                    </button>
                                )}
                                {onEditLead && (
                                    <button
                                        onClick={() => { setPencilMenuOpen(false); setLeadPickerOpen(true); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left font-body text-sm text-warm-900 hover:bg-mustard-soft/40 transition-colors"
                                        data-testid={`${testId}-menu-alert`}
                                    >
                                        <Bell size={13} className="text-terracotta" />
                                        Edit alert
                                        <span className="ml-auto text-[10px] uppercase tracking-widest text-warm-500">
                                            {(currentLead ?? 15) === 0 ? "off" : `${currentLead ?? 15}m`}
                                        </span>
                                    </button>
                                )}
                                {onAskCover && (
                                    <button
                                        onClick={() => { setPencilMenuOpen(false); onAskCover(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left font-body text-sm text-warm-900 hover:bg-mustard-soft/40 transition-colors"
                                        data-testid={`${testId}-menu-cover`}
                                    >
                                        <Star size={13} className="text-mustard" />
                                        Ask Mochi to cover
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {/* Click-outside catcher rendered as a sibling so AnimatePresence has a single direct child. */}
                    {pencilMenuOpen && (
                        <div
                            className="fixed inset-0 z-30"
                            onClick={() => setPencilMenuOpen(false)}
                        />
                    )}
                    <AnimatePresence>
                        {pomodoroChooserOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-30"
                                    onClick={() => setPomodoroChooserOpen(false)}
                                />
                                <PomodoroStartMenu
                                    onClose={() => setPomodoroChooserOpen(false)}
                                    onStart={(focusMin, breakMin) => {
                                        onStartFocus?.(focusMin, breakMin);
                                        setPomodoroChooserOpen(false);
                                    }}
                                    placement="left"
                                />
                            </>
                        )}
                    </AnimatePresence>
                    <AnimatePresence>
                        {leadPickerOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-30"
                                    onClick={() => setLeadPickerOpen(false)}
                                />
                                <motion.div
                                    key="lead-picker"
                                    {...POPOVER_MOTION}
                                    className="absolute left-0 top-full mt-1 z-40 bg-cream rounded-2xl shadow-float border border-warm-300/40 py-2 px-2 w-56"
                                    data-testid={`${testId}-lead-picker`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 px-1 pb-1.5">
                                        Alert before reminder
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {[0, 5, 15, 30, 60].map((mins) => {
                                            const active = (currentLead ?? 15) === mins;
                                            return (
                                                <button
                                                    key={mins}
                                                    onClick={async () => {
                                                        setLeadPickerOpen(false);
                                                        try { await onEditLead?.(mins); } catch (e) { console.warn("lead edit failed (parent shows toast)", e); }
                                                    }}
                                                    className={`font-display text-xs px-3 py-1.5 rounded-full transition-all active:scale-95 ${
                                                        active
                                                            ? "bg-terracotta text-cream"
                                                            : "bg-mustard-soft/40 hover:bg-mustard-soft text-warm-900"
                                                    }`}
                                                    data-testid={`${testId}-lead-${mins}`}
                                                >
                                                    {mins === 0 ? "Off" : `${mins}m`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </span>
            )}
        </div>
    );
}
